<!--

Copyright Paul B. Lucas (C) 2025  ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is used in the ITSupport role to disposition assets frcovered from employees.
Assets maybe placed back into inventory or surplused.   If the asset has a disk, then prior to
being surpused, asset must be cryptographically wiped.
Process:
    1.  Support tech selects assets from list - list generated on allowable orgs and filtered on
        status 'Removed-from-Service'.
    2.  Selected assets are listed on CONTINUE
    3.  Selected assets may be surplused or returned to inventory via command/button link
    4.  Prior to surplusing, user MUST certify disk has been wiped.
    5.  DISPOSE button effectuates action.

Revisions:
    03/24/2025      pblucas     initial creation
 

-->
<template>
    <div class="_master_container"> 

        <!--
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |   RENDERS ASSET LIST FOR ALLOWED ORGS WITH FILTERS.  SELECTION IS CHECKBOX AS FIRST LIST CELL
        |   Selection generates list of deployeble assets against selected steward
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        -->

        <div v-if="renderState == 'SELECT-ASSETS'">
            <div v-if="assets.length > 0">
                <div class="_list">
                    <p class="_p_list_header">Organizational Assets Recovered for {{ orgDisplay }}</p>
                </div>
                <div class="_list_container">
                    <div class="_table">
                        <!-- asset filter entry -->
                        <div class="_table_row">
                            <div class="_table_cell">
                                <p class="_p_list_header" >Filters:</p>
                            </div>                           
                            <div class="_table_cell">
                                <p class="_p_list_header" >&nbsp;</p>
                            </div>                           
                           <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['tag']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['asset-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['owner-org']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['it-data']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['serial-no']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['description']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['mfg']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['model']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['os-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <button class="_button_clear_filters" @click="clearAssetFilters">Clear Filters</button>
                            </div> 
                        </div>
                        <!-- asset table headers -->
                        <div class="_table_row">
                            <div class="_table_cell">
                                <p class="_p_list_header">Inventory</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Surplus</p>
                            </div> 
                           <div class="_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">IT Data</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Serial No</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">OS Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- asset property values -->
                        <div class="_table_row" v-for="(asset,idx) in filteredAssets" :key="filteredAssets[idx].uuid">
                            <div class="_table_check_cell">
                                <!-- naming button group on idx makes each row of radio buttons act independently -->
                                <input class="_check" type="radio" :name=idx checked v-model="filteredAssets[idx].dispo" 
                                    value="inventory"
                                    tabindex="0"
                                    aria-label="Check to re-inventory asset" 
                                    aria-describedby="Allows user to place assets back in inventory"  />
                            </div>
                            <div class="_table_check_cell">
                                <input class="_check" type="radio" :name=idx v-model="filteredAssets[idx].dispo" 
                                    value="surplus"
                                    tabindex="0"
                                    aria-label="Check to surplus ssets" 
                                    aria-describedby="Allows user to select assets for surplusing"  />
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx]['tag']}}
                            </div>
                           <div class="_table_cell">
                                {{ prettyType(filteredAssets[idx]['asset-type'])}}
                            </div>
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['owner-org'] }}
                            </div>  
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['it-data'] }}
                            </div>              
                            <div class="_table_cell" >
                                {{ filteredAssets[idx]['serial-no'] }}
                            </div>  
                            <div class="_table_cell_ellipsis">
                                {{ filteredAssets[idx].description }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].mfg }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx].model }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredAssets[idx]['os-type'] }}
                            </div>
                            <div class="_actions_table_cell">
                                <button class="_action_button" style="order:1;" v-if="filteredAssets[idx].dispo == 'surplus'"   
                                    tabindex="0" 
                                    aria-label="Select asset in list row for re-inventory" 
                                    aria-describedby="Select asset for re-inventory" 
                                    @click.stop="markAssetForInventory(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="markAssetForInventory(filteredAssets[idx], idx)">
                                    Re-Inventory Asset
                                </button>
                                <button class="_action_button" style="order:2;" v-if="filteredAssets[idx].dispo == 'inventory'"   
                                    tabindex="0" 
                                    aria-label="Select asset to surplus" 
                                    aria-describedby="Select asset to surplus" 
                                    @click.stop="markAssetForSurplus(filteredAssets[idx], idx)"
                                    @keydown.enter.stop="markAssetForSurplus(filteredAssets[idx], idx)">
                                    Surplus Asset
                                </button>
                               
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
            <div class="_no_assets_warning_container" v-else>
                <p class="_assets_warning" style="order: 1;">
                    There are no recovered assets for the allowed org list
                </p>
                <div class="_assets_warning_clear" style="order: 2;">
                    <button class="_main_button" 
                        tabindex="0"
                        aria-label="Clears filter list" 
                        aria-decribedby="Allow user option of clearing filters when no results show on current filters" 
                        @click="clearAssetFilters"
                        @keydown.enter="clearAssetFilters">Clear Filters</button>
                </div> 

            </div>      
        </div>
       <!--
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |   RENDERS SELECTED ASSET LIST FOR ASSETS MARKED AS SURPLUS.  ACTIONS: EDIT/VIEW
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        -->

        <div v-if="renderState == 'LIST-SURPLUSED-ASSETS'">
            <div v-if="surplusedAssets.length > 0">
                <div class="_list">
                    <p class="_p_list_header">Assets Marked for Surplus</p>
                </div>
                <div class="_list_container">
                    <div class="_table">
                        <!-- asset filter entry -->
                        <div class="_table_row">
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['tag']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['asset-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['owner-org']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['it-data']" @keyup="setFilters">
                            </div>
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['serial-no']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['description']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['mfg']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['model']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <input class="_input_filter" type="text" v-model="assetFilters['os-type']" @keyup="setFilters">
                            </div> 
                            <div class="_table_cell">
                                <button class="_button_clear_filters" @click="clearAssetFilters">Clear Filters</button>
                            </div> 
                        </div>
                        <!-- asset table headers -->
                        <div class="_table_row">
                            <div class="_table_cell">
                                <p class="_p_list_header">Tag</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Asset Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Owner Org</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">IT Data</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Serial No</p>
                            </div> 
                            <div class="_table_cell">
                                <p class="_p_list_header">Description</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Manufacturer</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">Model</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_header">OS Type</p>
                            </div>
                            <div class="_table_cell">
                                <p class="_p_list_actions_header">Actions</p>
                            </div>
                        </div>
                    
                        <!-- asset property values -->
                        <div class="_table_row" v-for="(asset,idx) in filteredSurplusedAssets" :key="filteredSurplusedAssets[idx].uuid">
                            <div class="_table_link_cell" style="text-align: left;" 
                                tabindex="0"
                                aria-label="Action link to view selected asset in list"  
                                aria-describedby="Link on assets tag to open view page on asset selected in list row"  
                                @click="viewSelectedAsset(filteredSurplusedAssets[idx], idx)"
                                @keydown.enter="viewSelectedAsset(filteredSurplusedAssets[idx],idx)">
                                {{ filteredSurplusedAssets[idx]['tag']}}
                            </div>
                            <div class="_table_cell">
                                {{ prettyType(filteredSurplusedAssets[idx]['asset-type'])}}
                            </div>
                            <div class="_table_cell" >
                                {{ filteredSurplusedAssets[idx]['owner-org'] }}
                            </div>  
                            <div class="_table_cell" >
                                {{ filteredSurplusedAssets[idx]['it-data'] }}
                            </div>              
                            <div class="_table_cell" >
                                {{ filteredSurplusedAssets[idx]['serial-no'] }}
                            </div>  
                            <div class="_table_cell_ellipsis">
                                {{ filteredSurplusedAssets[idx].description }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredSurplusedAssets[idx].mfg }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredSurplusedAssets[idx].model }}
                            </div>
                            <div class="_table_cell">
                                {{ filteredSurplusedAssets[idx]['os-type'] }}
                            </div>
                            <div class="_actions_table_cell">
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0" 
                                    aria-label="Edit asset in list row for deployment" 
                                    aria-describedby="Edit asset for deployment" 
                                    @click.stop="editSelectedAsset(filteredSurplusedAssets[idx], idx)"
                                    @keydown.enter.stop="editSelectedAsset(filteredSurplusedAssets[idx], idx)">Edit Asset</button>
                                <button class="_action_button" style="order:1;" 
                                    tabindex="0" 
                                    aria-label="View asset in list row for deployment" 
                                    aria-describedby="View asset for deployment" 
                                    @click.stop="viewSelectedAsset(filteredSurplusedAssets[idx], idx)"
                                    @keydown.enter.stop="viewSelectedAsset(filteredSurplusedAssets[idx], idx)">View Asset</button>
                              
                            </div>
                        </div>
                    </div>  <!-- end of table -->
                </div>      <!-- end of bordered container-->
            </div>
        </div>
        <div class="_instructions" v-if="renderState == 'LIST-SURPLUSED-ASSETS'">
            <p class="_p_instructions">
                The listed assets will be marked as surplus.  Before configurable assets (laptops, desktops, storage) can 
                be placed in surplus, you <b>must edit</b> the asset and certify that the disk has been wiped.  Preferentially,
                this is done via a cryptographically secure method, however degaussing is acceptable. <br/><br/>
                Certify by checking the 'disk wiped' box.   Note: The name and date of the action will be recorded. 

            </p>
        
        </div>
        <!-- 
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
        |
        |       VIEW EDIT ASSET FORM RENDERING
        |
        |>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>   
        -->
    
        <div v-if="renderState == 'EDIT-SELECTED-ASSET'">
            <div class="_saved_right" v-if="bAssetsSurplused">
                <p class="_p_saved">Assets Surplused</p>
            </div>

            <EditAsset  :key="surplusedAssets[viewIndexSurplused].uuid"
                        :asset="surplusedAssets[viewIndexSurplused]" 
                        :restriction="'WIPE-ASSETS'" 
                        @markWiped="markDiskAsWiped"/>
     
        </div>
        <div v-if="renderState == 'VIEW-SELECTED-ASSET'">
            <ViewAssetEntire :asset="surplusedAssets[viewIndexSurplused]" 
                             :key="surplusedAssets[viewIndexSurplused].uuid"/>
        </div>

        <div class="_page_controls">
            <!-- allow user to rescind employee selection -->
 
            <button class="_main_button"   type="button" v-if="renderState == 'SELECT-ASSETS'"
                    tabindex="0"
                    aria-label="Dispose selected assets" 
                    aria-describedby="Control to dispose selected assets"
                    @click="disposeSelectedAssets()"
                    @keydown.enter="disposeSelectedAssets()">Continue
            </button>
            <!-- allow user to rescind asset selection -->
            <button class="_main_button"   type="button" v-if="renderState == 'LIST-SURPLUSED-ASSETS'"
                    tabindex="0"
                    aria-label="Reset selected assets" 
                    aria-describedby="Control to reset selected assets"
                    @click="reselectAssets()"
                    @keydown.enter="reselectAssets()">Reselect Assets
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'LIST-SURPLUSED-ASSETS' && bOKtoSurplus"
                    tabindex="0"
                    aria-label="Issue selected assets" 
                    aria-describedby="Control to issue selectd assets"
                    @click="surplusAssets()"
                    @keydown.enter="surplusAssets()">Surplus Assets
            </button>
            <!-- controls observable from edit asset page -->
            <button class="_main_button"   type="button" v-if="renderState == 'EDIT-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="Cancel edit of asset" 
                    aria-describedby="Control to cancel edit of asset"
                    @click="cancelEdit()"
                    @keydown.enter="cancelEdit()">Cancel
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'EDIT-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="Save edited asset" 
                    aria-describedby="Control to save edited asset"
                    @click="saveAsset()"
                    @keydown.enter="saveAsset()">Save
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'EDIT-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="Cancel and return to list" 
                    aria-describedby="camcel and return to selected asset list"
                    @click="backToAssetList()"
                    @keydown.enter="backToAssetList()">Return to List
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'EDIT-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="edit next asset" 
                    aria-describedby="Control to edit next asset"
                    @click="nextAsset()"
                    @keydown.enter="nextAsset()">Next
            </button>

            <!-- viewing assets -->
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="view asset" 
                    aria-describedby="Control to view asset"
                    @click="nextAsset()"
                    @keydown.enter="nextAsset()">Next
            </button>
            <button class="_main_button"   type="button" v-if="renderState == 'VIEW-SELECTED-ASSET'"
                    tabindex="0"
                    aria-label="Cancel and return to list" 
                    aria-describedby="camcel and return to selected asset list"
                    @click="backToAssetList()"
                    @keydown.enter="backToAssetList()">Return to List
            </button>

        </div>


    </div>
    
</template>

<script>
    import {ref, computed}              from 'vue'
    import {useStore}                   from 'vuex'
    
    import EditAsset                    from '@/components/Assets/EditAsset.vue'
    import ViewAssetEntire              from '@/components/Assets/ViewAssetEntire.vue'

 
    // import composables
    import useAssetsAPI                 from '@/composables/useAssetsAPI'
    import useRefreshAPI                from '@/composables/useRefreshAPI'
    import usePermissionsAPI            from '@/composables/usePermissionsAPI'

    // import enumerations
    import SysConfigs                   from '@/configuration/form-options.json'
    import AdministrativeGroups         from '@/configuration/adminGroups.config.json'
    import * as XLSX                    from 'xlsx-js-style'   

    // pdf converter  install as npm install --save dom-to-pdf
    import domToPdf                     from 'dom-to-pdf'

    export default {
        components: {
           EditAsset,
           ViewAssetEntire 
        },
        props: [
            'claims',
            'gat'
        ],

        emits: [
        ],
    
        setup(props)    {
            
            const { assetResults,  fetchAssetsOnOrgs }      =   useAssetsAPI()           
            const store                                     =   useStore()
            const { checkStoreForRefresh }                  =   useRefreshAPI()
            const { checkAdminGroups }                      =   usePermissionsAPI() 

            //  store data
            const user                  =   computed( () => store.state.user)

            //  actions on store
            const setUser               = (u)    => store.dispatch('setUser', u)

            // security & permissions
            const sysConfigs            =   ref([])
            sysConfigs.value            =   SysConfigs
            const AdminGroups           =   ref([])
            AdminGroups.value           =   AdministrativeGroups['securityGroups']
            const aOrgs                 =   ref(Array.prototype.slice.call(user.value['allowed-orgs']))

            // use refs
            const assets                =   ref([])             // list of assets
            const asset                 =   ref({})             // individual asset to bew viewed/edited
            const surplusedAssets       =   ref([])             // listed pre-depoyment assets for employee
            const surplusedAsset        =   ref({})             // selected asset from list of selected Assets
            const backupSurplusedAsset  =   ref({})             // for cancel of edits on selected asset
            const viewIndexAsset        =   ref(0)              // asset
            const viewIndexSurplused    =   ref(0)              // selected asset
            const orgDisplay            =   ref(null)           // orgcode display for selection
            const renderState           =   ref('SELECT-ASSETS')
 
            const assetFilters           =   ref({
                'tag':                  "",
                'asset-type':            "",
                'owner-org':            "",
                'it-data':              "",
                'serial-no':            "",
                'description':          "",
                'mfg':                  "",
                'model':                "",
                'os-type':              ""
            })

         
            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            .then( async () => { 
                await fetchAssetsOnOrgs(aOrgs.value)        
                .then( () => {
                    let rawAssets  = assetResults.value.filter( (a) => a.status == 'Removed-from-Service') 
                    // add control properties to array
                    let recoveredAssets = rawAssets.map(o => ({...o, ...{"dispo": "inventory"}}))
                    assets.value = recoveredAssets
                })    
            })
            checkAdminGroups(props.claims)

            orgDisplay.value = user.value['allowed-orgs'].toString()

            const filteredAssets = computed( () => {

                let aFilteredValues = assets.value

                aFilteredValues = aFilteredValues.filter( (asset) => {
                    return asset['tag'].toLowerCase().match(assetFilters.value['tag'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['asset-type'].toLowerCase().match(assetFilters.value['asset-type'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['owner-org'].toLowerCase().match(assetFilters.value['owner-org'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['it-data'].toLowerCase().match(assetFilters.value['it-data'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['serial-no'].toLowerCase().match(assetFilters.value['serial-no'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['description'].toLowerCase().match(assetFilters.value['description'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['mfg'].toLowerCase().match(assetFilters.value['mfg'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['model'].toLowerCase().match(assetFilters.value['model'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['os-type'].match(assetFilters.value['os-type'].replace("*","\\*"))
                })
                return aFilteredValues
            })

            const filteredSurplusedAssets = computed( () => {

                let aFilteredValues = surplusedAssets.value

                aFilteredValues = aFilteredValues.filter( (asset) => {
                    return asset['tag'].toLowerCase().match(assetFilters.value['tag'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['asset-type'].toLowerCase().match(assetFilters.value['asset-type'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['owner-org'].toLowerCase().match(assetFilters.value['owner-org'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['it-data'].toLowerCase().match(assetFilters.value['it-data'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['serial-no'].toLowerCase().match(assetFilters.value['serial-no'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['description'].toLowerCase().match(assetFilters.value['description'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['mfg'].toLowerCase().match(assetFilters.value['mfg'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['model'].toLowerCase().match(assetFilters.value['model'].toLowerCase().replace("*","\\*"))
                })
                aFilteredValues = aFilteredValues.filter( (asset) =>  {
                    return asset['os-type'].match(assetFilters.value['os-type'].replace("*","\\*"))
                })
                return aFilteredValues
            })

            const bOKtoSurplus        =   computed( () => {
                let r = true
                surplusedAssets.value.forEach( function (asset, idx) {
                    // if asset is a config item with disk
                    if (    asset['asset-type'] == 'laptop'
                         || asset['asset-type'] == 'desktop computer' 
                         || asset['asset-type'] == 'usb attached storage'
                         || asset['asset-type'] == 'network attached storage'
                         || asset['asset-type'] == 'server') {
                        /*
                        // surplusing requirements to be set by IT SUPPORT
                        'disk-wiped'         
                        'disk-wiped-by'             // set in asset editor     
                        'disk-wiped-date'           // set in asset editor    
                        */
                        if(asset['disk-wiped'] == false)            r = false
                        if(asset['disk-wiped-by'].length == 0)      r = false
                        if(asset['disk-wiped-date'].length == 0)    r = false                    
                    }
                })               
                return r
            })

            return {user, assets, asset, surplusedAssets, filteredSurplusedAssets, surplusedAsset,
                    filteredAssets, assetFilters, sysConfigs, backupSurplusedAsset,
                    orgDisplay, setUser, AdminGroups, aOrgs, viewIndexAsset, viewIndexSurplused, 
                    bOKtoSurplus, renderState}
        },

        data() {
 
            return {
                // switch for page conditional rendering  
                savedFlag:          false,
                bAssetsSurplused:   false
            }
        },
        methods: {
            // Function: converts status enumeration to more readable 
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            },
            // FUNCTIONS:  Implement button control of selection radios
            markAssetForInventory(_, idx) {
                this.filteredAssets[idx].dispo  = 'inventory'
            },
            markAssetForSurplus(_, idx) {
                this.filteredAssets[idx].dispo  = 'surplus'
            },
            surplusAssets() {
                // store the assets
                const { assetKeyData, storeAsset} = useAssetsAPI()

                this.filteredSurplusedAssets.forEach( async (a) => {
                    a.status = 'Surplused'
                    await storeAsset(a)                    
                })

                this.bAssetsSurplused = true
                console.log( "AssetsDisposition:: surplusAssets: bAssetsSurplused: "+this.bAssetsSurplused)
            },
            async disposeSelectedAssets()     {

                const {assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

                let assetsToDispose = JSON.parse(JSON.stringify(this.filteredAssets))

                assetsToDispose.forEach( (a, idx) => {
                    if(a.dispo   == 'inventory') {
                        // set the status back to pending issue
                        assetsToDispose[idx].status = "Active-Pending-Issue"
                    }  
                    if(a.dispo   == 'surplus') {
                        // set the status back to pending issue
                        assetsToDispose[idx].status = "Surplus"
                    }                       
                })

                // placing assets back into inventory requires no further action
                let assetsToInventory = assetsToDispose.filter( (a) => a.status == 'Active-Pending-Issue')
                assetsToInventory.forEach( async (a) => {
                    await storeAsset(a)                    
                })
            
                // surplusing requires additional work
                this.surplusedAssets = assetsToDispose.filter( (a) => a.status == 'Surplus')
                this.renderState = 'LIST-SURPLUSED-ASSETS'
            },
            markDiskAsWiped(v) {
                let dt = new Date()
                if (v) {
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped']      =   true
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped-by']   =   this.user['name-concat']
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped-date'] =   dt.toString()
                }
                 else {
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped']      =   false
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped-by']   =   ""
                    this.surplusedAssets[this.viewIndexSurplused]['disk-wiped-date'] =   ""
                }
            },
            viewSelectedAsset(asset, idx) {
                this.viewIndexSelected = idx
                this.renderState = 'VIEW-SELECTED-ASSET'
            }, 
            editSelectedAsset(asset, idx) {
                this.viewIndexSelected = idx
                //this.selectedAsset  =   JSON.parse(JSON.stringify(this.surplusedAssets[idx]))
                this.surplusedAsset = this.surplusedAssets[idx]
                this.renderState = 'EDIT-SELECTED-ASSET'
            },
            reselectAssets() {
                this.assets.forEach( (a) => {
                    a.dispo = 'inventory'
                })
                this.renderState = 'SELECT-ASSETS'
                this.surplusedAssets = []
            },
            cancelEdit(asset, idx)    {
                this.filteredSurplusedAssets[this.viewIndexSurplused] = this.backupSurplusedAsset
            },
            async saveAsset() {
                // asset is filteredSeletecdAssets on on the selection view index
                const {assetResults, assetKeyData, fetchAssetsOnQuery, fetchAssetsOnOrgs, storeAsset} = useAssetsAPI()

                await storeAsset(this.filteredSurplusedAssets[this.viewIndexSurplused])
                .then(async () => {
                    await fetchAssetsOnQuery({id: assetKeyData.value[0].pkey})
                    .then( () => {
                        this.filteredSurplusedAssets[this.viewIndexSurplused]  = assetResults.value[0]    

                        this.renderState        =   'LIST-SURPLUSED-ASSETS'
                        this.savedFlag          =   true
                    
                    })
                }) 
            },
            backToAssetList() {
                this.renderState    =   "LIST-SURPLUSED-ASSETS"  
                this.savedFlag      =   false  
            },

            // MARK:  THE FOLLOWING METHODS ARE APPLIED TO VIEW/EDIT ELEMENTS TO RETURN TO OVERALL LIST - GENERAL USERS           
            // FUNCTION: increments through viewing index 
            nextAsset() {
                this.viewIndexSurplused++
                if (this.viewIndexSurplused >= this.filteredSurplusedAssets.length) this.viewIndexSurplused = 0
                this.savedFlag      =   false  
            },
            
            setFilters() {
                // deprecated for computed proxy in setup
            },
            clearAssetFilters() {
                this.assetFilters = {
                    'tag':                  "",
                    'asset-type':            "",
                    'owner-org':            "",
                    'it-data':              "",
                    'serial-no':            "",
                    'description':          "",
                    'mfg':                  "",
                    'model':                "",
                    'os-type':              ""
                }
            },

            // FUNCTION: Created a PDF of ViewEditEmployee rendering
            // id of element on DOM is '_pdf_render'
            // Need to created from the DOM and then do a standard download so that the PDF appears
            // in the browser downloads.
            async generatePDF() {
                
                // MARK:  generate the appropriate filename with embedded date
                const date = new Date()
                let year    =   date.getFullYear().toString()
                let month   =   date.getMonth()+1
                month       =   month.toString().padStart(2,'0')
                let day     =   date.getDate()
                day         =   day.toString().padStart(2,'0')
                let hours   =   date.getHours().toString().padStart(2,'0')
                let mins    =   date.getMinutes().toString().padStart(2,'0')

                 // 20240404_1559 format
                 const filedate = year+month+day+'_'+hours+mins
                 let empl = this.mutatableEmployee['name-concat']
                
                // MARK: - Since this is a list of multiple employees, we want to 
                // identify the agreement document with the employee's last name in
                // addition to the agreement number.  This is available in string form and we
                // want only the last name
                let aNames = empl.split(" ")
                let lName = aNames[aNames.length-1]
                const pdfFileName = lName+"-Employee-Record-"+filedate+".pdf"    

                // select and the container and add a title for teh PDF document              
                var element = document.getElementById('_pdf_render');
                var title   = document.createElement("p")
                var brk     = document.createElement("br")
                title.textContent = "Employee Record"
                title.style['font-family'] = "'Times New Roman',Times, serif"
                title.style['font-size'] = "16pt"
                title.style['font-weight'] = "bold"
                title.style['text-align'] = "center"
                title.appendChild(brk)
                title.appendChild(brk)
                var firstChild = document.getElementById("_pdf_row1")
                element.insertBefore(title,firstChild)

                var options = {
                    filename: pdfFileName,
                    excludeTagNames: ['button']
                };
                await domToPdf(element, options, function(pdf) {
                     
                    title.remove()
                });
                    
            }

          
        },      // end of methods list


        // hooks
        beforeMount() {

        },
        mounted() {
            //console.log("LE Mounted:: Number of employees Found: "+this.employees.length)
            //console.log("LE Mounted:: Employeess: "+JSON.stringify(this.employees))
        },
        updated() {
            //console.log("DeployAssets:: Updated: bOKToIssue "+this.bOKtoIssue)
            //console.log("DeployAssets:: Updated: renderState: "+this.renderState)
            //console.log("LE Update:: Number of employees found: "+this.employees.length)
            //console.log("LE Update:: Employeess: "+JSON.stringify(this.employees))

        }
    }
</script>

<style scoped>

._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 50%;
        margin: 25px 0 0 0;
        padding: 0;
        overflow-x: auto;
    }
    ._list_container {
        width: 95%;
        overflow-x: auto;
        margin: 0 auto 0;
        padding: 0;
        border-style: solid;
        border-width: 2px;
        border-color:#13294B;
        background-color: white;
    }
    ._list {
        width: 100%;
        margin: 0;
        padding: 0;
    }
    ._p_selected_name {
        margin: 0 0 10px 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 1.0em;
        font-weight: bold;
    }
    ._p_instruction_header {
        margin: 0 0 10px;
        padding: 2px 5px;
        color: #13294B;
        font-size: 1.0em;
        font-weight: bold;
        text-align: center;
    }
    ._p_list_header {
        margin: 0;
        padding: 2px 5px;
        color: #13294B;
        font-size: 0.9em;
        font-weight: bold;
        text-align: center;
    }

    /* list action button styling */
    ._action_button {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:#13294B;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button:hover {
        background-color: #FF5F05;
    }
    ._action_button_remove {
        border-style: solid;
        border-color: #13294B;
        border-width: 1px;
        border-radius: 4px;
        background-color:red;
        color: white;
        margin: 0 5px;
        padding: 0 5px;
        font-size: 0.8em;
    }
    ._action_button_remove:hover {
        background-color: white;
        color: red;
    }
    ._instructions {
        width: 80%;
        margin: 25px auto;
        padding: 2px;
     }
    ._p_instructions {
        text-align: justify;
        color: #13294B;
        margin: 0;
        padding: 2px;
    }
   
    ._page_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 15px 0 20px 0;
        padding: 0 5px;;
    }
    ._page_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        margin: 0;
        padding: 0 5px;
    }
    ._check {    
        margin: 0;
        padding: 0 6px;
    }
    ._check_label {
        margin: 0;
        padding: 0 6px;
    }
    /* employees list styling */
    ._table {
        width: 100%;
        margin: 0;
        display: table;
    }

    ._table_row   {
        background-color:#BFC9D9;
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        overflow: hidden;
        height: 25px;
        white-space: nowrap;
        width: 100%;
        margin: 0;
    }

    ._table_row:nth-child(2n+2) {
        background-color:#a4b9dc;
    }
    ._table_header_cell {
        display: table-cell;
        min-width: 10%;
        text-align: center;
        margin: 0;
        padding: 2px 0 2px 0;
    }
    ._table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 25px;
    }
    ._actions_table_cell {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content:space-between;
        margin: 0 auto;
    }
    ._table_link_cell  {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        color: #13294B;
        text-decoration: none;
        margin: 0 0 0 20px;
        padding: 2px 0 2px 10px;
    }
    ._table_link_cell:hover  {
        color: #FF5F05;
    }
    ._employee_name_tab_cell {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
    }
    ._employee_name_tab_cell:hover {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
        font-weight: normal;
        color: #13294B;
    }
    ._p_column_headers {
        font-size: 1.0em;
        font-weight: bold;
        color: #13294B;
        text-align: center;
        margin: 2px 0;
        padding: 5px;
    }
 
    ._employees_warning {
        text-align: center;
        color: #FF5F05;
        margin: 25px auto;
        padding: 5px;
    }

    ._issue_notice {
        float: right;
        margin: 0 15ps 0 0;
        padding: 0 2px;
        color: #FF5F05;
    }
    ._assets_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
    }
    ._assets_inline_controls {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        /* display: inline; */
        margin: 25px 0 0 0;
        padding: 0 5px;
    }
    ._no_assets_warning_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 20px;
        margin: 10px 0;
    }
    ._assets_warning {
        color: #FF5F05;
    }
    _._assets_warning_clear {
        float: right;
    }    
    
    /* assets list styling */
    ._table {
        display: table;
        margin: 0;
    }

    ._table_row   {
        background-color:#BFC9D9;
        display: table-row;
        min-width: 40px;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        overflow: hidden;
        height: 25px;
        white-space: nowrap;
    }

    ._table_row:nth-child(2n+2) {
        display: table-row;
        min-width: 40px;
        text-align: center;
        margin: 0;
        background-color:#a4b9dc;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
    }
 
    ._table_header_cell {
        display: table-cell;
        min-width: 10%;
        text-align: center;
        margin: 0;
        padding: 2px 0 2px 0;
    }
    ._table_check_cell {
        display: table-cell;
        min-width: 10%;
        text-align: center;
        margin: 0;
        padding: 2px 0 2px 0;
    }
    ._table_cell  {
        display: table-cell;
        min-width: 10%;
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 30px;
    }
    ._table_cell_ellipsis {
        /* display: table-cell; */
        max-width: 80%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        
        text-align: left;
        margin: 0;
        padding: 2px 10px 2px 10px;
        
    }
    ._asset_actions_table_cell {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content:space-between;
        margin: 0 auto;
    }
    ._assets_tag_table_cell {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
    }
    ._assets_tag_table_cell:hover {
        display: table-cell;
        min-width: 40px;
        text-align: left;
        margin: 0;
        padding: 2px 0 2px 10px; 
        font-weight: bold;
        /* color: #FF5F05; */
    }
    ._input_filter {
        width: 70%;
        background-color: white;
        color: black;
        font-size: 0.9em;
        font-weight: normal;
        border-style: solid;
        border-radius: 3px;
        border-width: 1px;
        border-color: #13294B;
        margin: 0 3px 0;
        padding: 2px;
    }
 
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._button_clear_filters {
        background-color:#13294B;
        color: white;
        font-size: 0.9em;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 0 5px;
        padding: 0 5px;
    }

    ._button_clear_filters:hover  {
        background-color: #FF5F05;
        color: white;
    }   
    ._p_saved {
        font-size: 16pt;
        color: #FF5F05;
        padding: 2px;
    }
    ._saved_right {
        padding: 0;
        margin: 0 10px 0 0;
        float: right;
    }
    ._saved_center {
        padding: 0;
        margin: 3px 0;
        text-align: center;
    }
    ._p_templated    {
        padding: 0;
        margin: 0;
    }
    ._p_template {
        margin: 5px auto 5px;
        padding: 0;
        color: #13294B;
        font-size: 12pt;
    }
    ._import_fieldset {
        width: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0;
        padding: 0;
    }
    ._row {
        width: 70%;
        margin: 0 auto;
        text-align: center;
    }
    ._radio {
        display: inline-block;
        width: 16px;
        margin: 0;
        /* position: relative; 
        top: 2px; 
        */
    }
    ._item_label {
        display: inline-block;
        width: 100%; 
        font-size: 0.75em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 0;
        padding: 0 0 0 10px;
    }
    ._col_30 {
        display: inline-block;
        width: 30%;
        margin: 0;
        padding: 0;
    }
    ._col_33 {
        display: inline-block;
        width: 33%;
        margin: 0;
        padding: 0;
    }
    ._col_67 {
        display: inline-block;
        width: 67%;
        margin: 0 auto;
        padding: 0;
    }
    ._col_70 {
        display: inline-block;
        width: 70%;
        margin: 0 auto;
        padding: 0;
    }
    /* sub-columns for radio buttons and labels */
    ._scol_15 {
        display: inline-block;
        width: 15%;
        margin: 0;
        padding: 0;
    }
    ._scol_25 {
        display: inline-block;
        width: 25%;
        margin: 0;
        padding: 0;
    }
    ._scol_80 {
        display: inline-block;
        text-align: left;
        width: 80%;
        margin: 0;
        padding: 0;
    }
    ._scol_85 {
        display: inline-block;
        text-align: left;
        width: 85%;
        margin: 0;
        padding: 0;
    }
     /* sub-columns for enclosing column radio buttons and labels */
    ._scol_33 {
        display: inline-block;
        text-align: left;
        width: 33%;
        margin: 0 auto;
        padding: 0;
    }
    @media screen and (max-width: 800px) {
        ._import_fieldset {
            flex-wrap: wrap;  
        }
        ._col_30 {
            width: 100%;
        }
        ._col_33 {
            width: 80%;
        }  
        ._col_67 {
            width: 100%;
        }
        ._col_70 {
            width: 80%;
            margin: 0 auto;
        }  
    }
    @media screen and (max-width: 620px) {
        ._col_33 {
            width: 100%;
            margin-top: 0;
        }
       ._scol_25 {
            width: 100%;
            text-align: center;
        }
        ._scol_33 {
            width: 100%;
            text-align: center;
        }

        ._col_67 {
            width: 50%;
            margin: 0 auto;
        }  
        
    }
</style>